import { LogOut, User as UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import useAuth from '@/hooks/useAuth';
import { ROUTES } from '@/utils/constants';

const initialsOf = (name?: string): string => {
  if (!name) return 'U';
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || 'U'
  );
};

interface TopBarUserProps {
  // Where "my profile" goes for THIS workspace. Every workspace has its own
  // profile route rendering the same ProfilePage, so the shell that owns the
  // route is the only thing that knows which one applies.
  profileRoute: string;
  // What to show under the name: the job role for most workspaces, the system
  // role for the admin console.
  roleLabel?: string | null;
  // Workspace-specific action rendered between the profile and logout buttons -
  // today only the admin console's "Back to Sales RM" shortcut.
  extra?: ReactNode;
}

/**
 * The top-right identity block, shared by all four workspace shells.
 *
 * Fix 8 replaced a dropdown menu here with two plain buttons. The dropdown hid
 * Logout - the single most-used control in the bar - behind a click on an
 * unlabelled avatar, and each of the four shells had its own hand-rolled copy of
 * it, so they had already drifted apart (different separators, different items,
 * one with a duplicated profile entry). Both actions are now visible and always
 * one click away, and there is exactly one implementation to keep correct.
 *
 * Logout is deliberately the LAST element and visually distinct: it is
 * destructive-adjacent (it ends the session) and must never be somewhere a
 * finger lands on the way to something else.
 */
export const TopBarUser = ({ profileRoute, roleLabel, extra }: TopBarUserProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = (): void => {
    void logout().then(() => navigate(ROUTES.LOGIN));
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      {extra}

      {/* The avatar + name IS the profile link now, rather than a menu trigger.
          It carries an explicit aria-label because on small screens the name and
          role are hidden and the button would otherwise announce only initials. */}
      <Button
        variant="outline"
        size="sm"
        className="h-auto shrink-0 gap-2 py-1.5"
        onClick={() => {
          void navigate(profileRoute);
        }}
        aria-label={`My profile${user?.name ? ` - ${user.name}` : ''}`}
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
          {initialsOf(user?.name)}
        </span>
        <span className="hidden text-left leading-tight md:block">
          <span className="block text-xs font-bold">{user?.name}</span>
          <span className="block text-[10px] font-semibold text-primary">{roleLabel}</span>
        </span>
        <UserIcon aria-hidden className="hidden h-4 w-4 shrink-0 text-muted-foreground md:block" />
      </Button>

      {/* Icon-only under `sm` so the bar still fits on a phone, but never
          icon-only WITHOUT a label - the aria-label carries the name either way. */}
      <Button
        variant="outline"
        size="sm"
        className="h-auto shrink-0 gap-1.5 py-1.5"
        onClick={handleLogout}
        aria-label="Logout"
      >
        <LogOut aria-hidden className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">Logout</span>
      </Button>
    </div>
  );
};
